/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Tag, Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@mesh.ai/rabbit';
import TagIcon from 'components/ObjectiveTagList/TagIcon';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';

import { ErrorOutline, KeyResultPrivate2, PrivateObjective } from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';
import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import { OBJECTIVE_LEVEL_TAGS, OKR_PROGRESS_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';

import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const isEllipsisActive = (containerElement) => containerElement?.offsetHeight < containerElement?.scrollHeight;

const TruncatedTextBodyMedium = styled(TextBodyMedium)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const COLOR_MAP = {
  ON_TRACK: 'tag-green',
  OFF_TRACK: 'tag-yellow',
  AT_RISK: 'tag-red',
  CLOSED_DROPPED: 'tag-darkGray',
  CLOSED_NOT_ACHIEVED: 'tag-lightGray',
  CLOSED_ACHIEVED: 'tag-lightGreen',
  CLOSED: 'tag-darkGray',
};

const TEXT_MAP = {
  ON_TRACK: 'On-track',
  OFF_TRACK: 'Off-track',
  AT_RISK: 'At-risk',
  CLOSED: 'Closed',
  CLOSED_ACHIEVED: 'Closed & achieved',
  CLOSED_DROPPED: 'Closed & dropped',
  CLOSED_NOT_ACHIEVED: 'Closed & not achieved',
};

const PERCENTAGE_COLOR_MAP = {
  ON_TRACK: '#44812F',
  OFF_TRACK: '#B96400',
  AT_RISK: '#C62C02',
  CLOSED: '#333333',
  CLOSED_ACHIEVED: '#1A5724',
  CLOSED_DROPPED: '#666666',
  CLOSED_NOT_ACHIEVED: '#A12402',
};

const ObjectiveTypeIcon = ({ objectiveType, departmentName }) => (
  <Flex mt="2px" alignItems="center">
    <TagIcon tagId={objectiveType} />
    <TextLabel ml="8px" color="text.g2">
      {OBJECTIVE_LEVEL_TAGS.find(({ value }) => value === objectiveType)?.label}
    </TextLabel>
    {objectiveType === 'DEPARTMENT' && departmentName ? (
      <Flex alignItems="center" marginLeft="6px">
        <Flex background="#666666" height="4px" width="4px" borderRadius="50%" />
        <TextLabel ml="8px" color="text.g2">
          {departmentName}
        </TextLabel>
      </Flex>
    ) : null}
  </Flex>
);

export const TableCell = ({ data }) => {
  const theme = useTheme();
  const { title, type, visibilityType, departmentName, unMasked, avgProgressPercentage, progressLevel, achieved } =
    data ?? {};
  const isObjective = type === 'OBJECTIVE';

  const roundedOffAvgProgressPercentage = Math.floor(avgProgressPercentage);

  const achievedValue = Math.floor(achieved);

  const isPrivateOKR = visibilityType === 'PRIVATE';

  const [showTooltip, setShowTooltip] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars

  const measuredRef = (node) => {
    if (node !== null) {
      const pTag = node.querySelector('.truncated-ellipsis-tooltip');
      setShowTooltip(isEllipsisActive(pTag));
    }
  };

  const unmaskedText = title;

  const { moduleName } = useOrganisationSettings();

  if (!unMasked) {
    return (
      <Box w="100%">
        <Flex alignItems="flex-start">
          <KeyResultPrivate2 color="#4657AE" boxSize="24px" />
          <Flex ref={measuredRef} ml="6px" flexDir="column" w="100%">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <TruncatedTextBodyMedium
                    className="highlight-able-text truncated-ellipsis-tooltip"
                    mb={isObjective ? '1px' : 0}
                  >
                    {title}
                  </TruncatedTextBodyMedium>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent>{unmaskedText}</TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          </Flex>
        </Flex>
      </Box>
    );
  }

  /* 
    find and report errors in okr
    1. if tracking type is dependent and okr has no children
    2. if tracking type is derived_sum and children are not summable.
  */

  const isDerivedSum = OKR_PROGRESS_TYPES.DERIVED_SUM === data?.progressType;
  const isDerivedAvg = OKR_PROGRESS_TYPES.DERIVED_AVG === data?.progressType;
  const okrHasNoChildren = data?.ifGoalChildExists === false;
  const areChildSummable = data?.areChildSummable !== false;

  const isTrackedAsDependentWithNoChildren = (isDerivedAvg || isDerivedSum) && okrHasNoChildren;

  const isTrackedAsDerivedSumWithNonSummableChildren = isDerivedSum && !areChildSummable;

  const hasError = isTrackedAsDependentWithNoChildren || isTrackedAsDerivedSumWithNonSummableChildren;

  let errorMessage = '';

  if (hasError && isTrackedAsDependentWithNoChildren) {
    errorMessage = `${moduleName} is tracked as 'Dependent' but has no child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )}`;
  }

  if (hasError && isTrackedAsDerivedSumWithNonSummableChildren) {
    errorMessage = `${moduleName} is tracked as 'Dependent - Sum' but has child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} whose metric/measures are not summable`;
  }

  return (
    <Box w="100%" mt={2}>
      <Flex alignItems="flex-start">
        {isObjective ? (
          isPrivateOKR ? (
            <PrivateObjective color="#7651BC" boxSize="24px" />
          ) : (
            <Objective color="#7651BC" boxSize="24px" />
          )
        ) : isPrivateOKR ? (
          <KeyResultPrivate2 color="#4657AE" boxSize="24px" />
        ) : (
          <KeyResult color="#4657AE" boxSize="24px" />
        )}

        <Flex ml="6px" flexDir="column" w="90%" ref={measuredRef} flexGrow={1}>
          <Flex w="100%" flexWrap="nowrap">
            {hasError ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Box as="span" lineHeight="20px">
                      <ErrorOutline
                        style={{ marginRight: '8px', marginBottom: '2px' }}
                        boxSize="16px"
                        color={theme.colors.alerts.negative}
                      />
                    </Box>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent>{errorMessage}</TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <TruncatedTextBodyMedium
                    className="highlight-able-text truncated-ellipsis-tooltip"
                    mb={isObjective ? '1px' : 0}
                  >
                    {title}
                  </TruncatedTextBodyMedium>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent>{unmaskedText}</TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          </Flex>

          {isObjective && <ObjectiveTypeIcon objectiveType={data?.objectiveType} departmentName={departmentName} />}
          <Flex mb={1} mt={2} alignItems="center">
            <Tag colorScheme={COLOR_MAP[progressLevel]}>{TEXT_MAP[progressLevel]}</Tag>
            <TextLabel color={PERCENTAGE_COLOR_MAP[progressLevel]} bold ml={3}>
              {roundedOffAvgProgressPercentage ?? 0}%
            </TextLabel>
            <TextLabel bold ml={3}>
              &bull; #{achievedValue ?? 0}
            </TextLabel>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
